import { Button, Icons, Input, Label, SecretInput } from '@repo/ui'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import axios from 'axios'
import { Loader2Icon } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { useAuth } from '@/hooks/use-auth'
import { authApi } from '@/lib/api'
import type { Membership } from '@/lib/api-types'
import { webUrl } from '@/lib/web-url'

export const Route = createFileRoute('/entrar')({
  validateSearch: (search: Record<string, unknown>) => ({
    returnTo: typeof search.returnTo === 'string' ? search.returnTo : undefined,
  }),
  component: LoginPage,
})

function safeReturnTo(returnTo: string | undefined, hasOrg: boolean): string {
  if (!hasOrg) return '/conversas'
  if (!returnTo) return '/conversas'

  let candidate = returnTo
  for (let i = 0; i < 5; i += 1) {
    try {
      const decoded = decodeURIComponent(candidate)
      if (decoded === candidate) break
      candidate = decoded
    } catch {
      break
    }
  }

  if (!candidate.startsWith('/')) return '/conversas'

  try {
    const url = new URL(candidate, window.location.origin)
    if (url.pathname === '/entrar') return '/conversas'
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return '/conversas'
  }
}

function LoginPage() {
  const navigate = useNavigate()
  const { returnTo } = useSearch({ from: '/entrar' })
  const { refresh, user, loading, currentOrgId, memberships } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  async function signInWithGoogle(callbackURL: string) {
    const res = await authApi.post<{ url?: string; redirect?: boolean }>(
      '/api/auth/sign-in/social',
      { provider: 'google', callbackURL }
    )
    if (res.data?.url) {
      window.location.href = res.data.url
    }
  }

  async function handleGoogleLogin() {
    setGoogleLoading(true)
    try {
      const target = safeReturnTo(returnTo, true)
      await signInWithGoogle(`${window.location.origin}${target}`)
    } catch {
      toast.error('Não foi possível iniciar o login com Google.')
      setGoogleLoading(false)
    }
  }

  useEffect(() => {
    if (!loading && user) {
      const hasOrg = !!currentOrgId || memberships.length > 0
      void navigate({ to: safeReturnTo(returnTo, hasOrg) as never })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, loading, memberships.length, user])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)

    try {
      await authApi.post('/api/auth/sign-in/email', {
        email,
        password,
        rememberMe: true,
      })
      await refresh()
      let nextMemberships: Membership[] = []
      try {
        const { data } = await authApi.get<Membership[]>(
          '/v1/users/me/memberships'
        )
        nextMemberships = data ?? []
      } catch {
        nextMemberships = []
      }
      toast.success('Bem-vindo de volta!')
      await navigate({
        to: safeReturnTo(returnTo, nextMemberships.length > 0) as never,
      })
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        String(
          error.response?.data?.message ?? error.response?.data?.error ?? ''
        )
          .toLowerCase()
          .includes('credential account not found')
      ) {
        toast.error('Essa conta usa login com Google.', {
          description: 'Use o botão Entrar com Google para acessar o Inbox.',
        })
      }
      setSubmitting(false)
      return
    }

    setSubmitting(false)
  }

  return (
    <div className="bg-background flex min-h-dvh items-center justify-center px-4 py-8">
      <div className="border-border bg-card w-full max-w-sm rounded-2xl border p-8 shadow-sm">
        <h1 className="text-2xl font-bold tracking-tight">Entrar</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Acesse seu atendimento no AgroSDR Inbox
        </p>

        <div className="mt-6">
          <Button
            type="button"
            variant="outline"
            className="h-11 w-full"
            disabled={googleLoading || submitting}
            onClick={() => void handleGoogleLogin()}
          >
            {googleLoading ? (
              <Loader2Icon className="size-4 animate-spin" />
            ) : (
              <Icons.google />
            )}
            Entrar com Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="bg-border h-px flex-1" />
            <span className="text-muted-foreground text-xs">ou</span>
            <div className="bg-border h-px flex-1" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                required
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <Label htmlFor="password">Senha</Label>
                <a
                  href={webUrl('/esqueci-senha')}
                  className="text-primary text-xs font-medium hover:underline"
                >
                  Esqueci minha senha
                </a>
              </div>
              <SecretInput
                id="password"
                visibilityLabel="senha"
                required
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            <Button type="submit" className="h-11 w-full" disabled={submitting}>
              {submitting ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : null}
              Entrar
            </Button>
          </form>
          <p className="text-muted-foreground mt-6 text-center text-sm">
            Ainda não tem conta?{' '}
            <a
              href={webUrl('/cadastrar')}
              className="text-primary font-medium hover:underline"
            >
              Criar conta
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
